import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../lib/client';
import { useAuthStore } from '../hooks/useAuth';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const setUser = useAuthStore((state) => state.setUser);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    setError(null);

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (authError) {
        setError(authError.message);
        return;
      }

      if (authData.user) {
        // Try to fetch user profile
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('auth_id', authData.user.id)
          .single();

        if (userData && !userError) {
          setUser(userData);
          navigate('/');
        } else {
          // User profile doesn't exist, create it
          console.log('User profile not found, creating...');
          
          const fullName = authData.user.user_metadata?.full_name || authData.user.email?.split('@')[0] || 'User';
          const orgSlug = authData.user.email?.split('@')[0]?.toLowerCase().replace(/[^a-z0-9]/g, '') + '-' + Date.now();
          
          try {
            // Create organization
            const { data: orgData, error: orgError } = await supabase
              .from('organizations')
              .insert({
                name: fullName + "'s Organization",
                slug: orgSlug,
              })
              .select()
              .single();

            if (orgError) throw orgError;

            // Create user profile
            const { data: newUserData, error: createError } = await supabase
              .from('users')
              .insert({
                auth_id: authData.user.id,
                organization_id: orgData.id,
                email: authData.user.email || '',
                full_name: fullName,
                role: 'admin',
              })
              .select()
              .single();

            if (createError) throw createError;

            console.log('Created user profile:', newUserData);
            setUser(newUserData);
            navigate('/');
          } catch (createErr: any) {
            console.error('Error creating user profile:', createErr);
            setError('Account exists but profile creation failed. Please contact support.');
          }
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-center">Sign in to your account</h2>
      
      {error && (
        <div className="mb-4 p-3 text-sm bg-destructive/10 text-destructive rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <input
            type="email"
            {...register('email')}
            className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="you@example.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-destructive">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Password</label>
          <input
            type="password"
            {...register('password')}
            className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="••••••••"
          />
          {errors.password && (
            <p className="mt-1 text-sm text-destructive">{errors.password.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {loading ? 'Signing in...' : 'Sign in'}
        </button>
      </form>

      <p className="mt-4 text-center text-sm text-muted-foreground">
        Don't have an account?{' '}
        <Link to="/register" className="text-primary hover:underline">
          Sign up
        </Link>
      </p>
    </div>
  );
}
